import React, { useEffect, useState } from 'react';
import { useDownloads } from '../context/DownloadsContext';

function Downloads() {
  const { downloads, isVisible, clearAllDownloads, cancelDownload } = useDownloads();
  const [shouldRender, setShouldRender] = useState(false);

  // Handle mount/unmount for smooth animations
  useEffect(() => {
    if (isVisible && downloads.length > 0) {
      // Small delay to trigger slide-in animation
      const timer = setTimeout(() => {
        setShouldRender(true);
      }, 10);
      return () => clearTimeout(timer);
    } else if (!isVisible && downloads.length === 0 && shouldRender) {
      // Wait for slide-out animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isVisible, downloads.length, shouldRender]);

  if (downloads.length === 0 && !shouldRender) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'downloading':
        return (
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'completed':
        return (
          <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'downloading':
        return 'Downloading...';
      case 'completed':
        return 'Completed';
      case 'error':
        return 'Failed';
      default:
        return '';
    }
  };

  return (
    <div 
      className={`fixed z-50 transition-all duration-300 ease-out
        bottom-4 left-4 right-4 md:left-4 md:right-auto
        max-w-md md:w-full w-auto
        ${
          shouldRender && isVisible 
            ? 'translate-y-0 opacity-100 md:translate-x-0' 
            : 'translate-y-full opacity-0 md:translate-y-0 md:-translate-x-full'
        }`}
    >
      <div className="bg-gradient-to-br from-dark-surface/70 via-dark-card/60 to-dark-border/50 backdrop-blur-xl border border-white/10 rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden">
        <div className="px-3 py-2.5 md:px-5 md:py-3.5 bg-white/5 backdrop-blur-sm border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <h3 className="text-white font-semibold text-xs md:text-sm">Downloads</h3>
            <span className="text-white/50 text-xs bg-white/5 px-1.5 py-0.5 md:px-2 rounded-full">{downloads.length}</span>
          </div>
          <button
            onClick={() => clearAllDownloads()}
            className="text-white/50 hover:text-white transition-all duration-200 p-1 md:p-1.5 hover:bg-white/10 rounded-lg"
            aria-label="Close downloads"
          >
            <svg className="h-3.5 w-3.5 md:h-4 md:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="max-h-64 md:max-h-96 overflow-y-auto">
          {downloads.map((download) => (
            <div
              key={download.id}
              className={`px-3 py-2.5 md:px-5 md:py-3.5 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-all duration-300 ${
                download.exiting 
                  ? 'opacity-0 transform translate-y-2 md:-translate-x-full' 
                  : 'opacity-100 transform translate-y-0 md:translate-x-0'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 md:gap-2.5 mb-1.5 md:mb-2">
                  <div className="flex-shrink-0">
                    {getStatusIcon(download.status)}
                  </div>
                  <p className="text-white/90 text-xs md:text-sm font-medium truncate">
                    {download.fileName}
                  </p>
                </div>
                
                {download.status === 'downloading' && (
                  <div className="mt-2 md:mt-2.5 space-y-1.5 md:space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-white/50 text-xs font-medium">
                        {getStatusText(download.status)}
                      </span>
                      <span className="text-white/70 text-xs font-semibold">
                        {Math.round(download.progress)}%
                      </span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5 md:h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-accent-blue to-accent-cyan h-1.5 md:h-2 rounded-full transition-all duration-300 shadow-lg shadow-accent-blue/30"
                        style={{ width: `${download.progress}%` }}
                      ></div>
                    </div>
                    <button
                      onClick={() => cancelDownload(download.id)}
                      className="text-red-400/80 hover:text-red-300 text-xs font-medium transition-all duration-200 hover:underline"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                {download.status === 'completed' && (
                  <div className="mt-1 md:mt-1.5">
                    <p className="text-green-400/90 text-xs font-medium">
                      {getStatusText(download.status)}
                    </p>
                  </div>
                )}
                
                {download.status === 'error' && (
                  <div className="mt-1 md:mt-1.5">
                    <p className="text-red-400/90 text-xs font-medium">
                      {download.error || getStatusText(download.status)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Downloads;

